import { useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  ButtonIcon,
  Card,
  Column,
  Columns,
  Heading,
  IconDelete,
  Stack,
  Text,
  TextLink,
  useToast,
} from 'braid-design-system';

import CustomLoader from 'src/components/CustomLoader/CustomLoader';
import { getConfigValues } from 'src/config';
import {
  DecodeSearchCriteriaDocument,
  DeleteSavedSearchDocument,
  GetSavedSearchesDocument,
} from 'src/generated/graphql';
import { useCookiesStrict } from 'src/hooks/cookies';

import { District } from './components/District';
import { Industry } from './components/Industry';
import { Keywords } from './components/Keywords';
import { NoSavedSearches } from './components/NoSavedSearches';
import { OpportunityType } from './components/OpportunityType';
import { Value } from './components/Value';

const SavedSearches = () => {
  const { loading, error, data } = useQuery(GetSavedSearchesDocument);
  const [deleteSavedSearch] = useMutation(DeleteSavedSearchDocument, {
    refetchQueries: [{ query: GetSavedSearchesDocument }],
  });
  const showToast = useToast();
  const baseUrl = getConfigValues().HEADER_SERVER_BASE;

  const { cookies, removeCookie } = useCookiesStrict(['SavedSearches']);

  if (loading) {
    return <CustomLoader />;
  }

  if (error || !data) {
    return (
      <Alert tone="critical">
        <Text>
          There was an error loading your saved searches. If this issue persists
          please{' '}
          <TextLink
            href="https://www.seekbusiness.com.au/contact-us"
            hitArea="large"
          >
            contact us
          </TextLink>
        </Text>
      </Alert>
    );
  }

  if (data.savedSearches.length === 0) {
    return <NoSavedSearches />;
  }

  const handleDeleteSavedSearch = async (
    id: string,
    searchName: string,
    searchUrl: string,
  ) => {
    try {
      await deleteSavedSearch({
        variables: {
          input: {
            id,
            searchName,
            searchUrl,
          },
        },
      });

      if (cookies.SavedSearches) {
        // This removes the entire cookie which means all interim saved searches
        // (not just the selected) will be removed. We're fine with this as the
        // cookie is short-lived and will quickly be made redundant by the eventually
        // consistent database store, and thus is likely to already be redundant
        removeCookie('SavedSearches', { path: '/' });
      }

      showToast({
        data: { testId: 'savedSearchDeletedToast' },
        tone: 'positive',
        message: 'Saved search deleted',
      });
    } catch {
      showToast({
        tone: 'critical',
        message: 'Something went wrong',
        description:
          'We encountered an error deleting this saved search. Please try again.',
      });
    }
  };

  return (
    <Stack space="small">
      {data.savedSearches.map((search, index) => (
        <Box id="profile-saved-search-row" key={index}>
          <Card>
            <Columns space="gutter">
              <Column>
                <Stack space="gutter">
                  <Heading level="4" component="h3">
                    <TextLink
                      href={`${baseUrl}${search.searchUrl}`}
                      target="_blank"
                    >
                      {search.searchName
                        ? search.searchName
                        : 'Untitled Search'}
                    </TextLink>
                  </Heading>
                  <SavedSearchFields searchUrl={search.searchUrl} />
                </Stack>
              </Column>

              <Column width="content">
                <Box height="full">
                  <ButtonIcon
                    id={`profile-saved-search-delete-row-btn-${search.id}`}
                    onClick={() =>
                      handleDeleteSavedSearch(
                        search.id,
                        search.searchName,
                        search.searchUrl,
                      )
                    }
                    icon={<IconDelete tone="critical" />}
                    label="Delete saved search"
                  />
                </Box>
              </Column>
            </Columns>
          </Card>
        </Box>
      ))}
    </Stack>
  );
};

const SavedSearchFields = ({ searchUrl }: { searchUrl: string }) => {
  const baseUrl = getConfigValues().HEADER_SERVER_BASE;
  const { loading, error, data } = useQuery(DecodeSearchCriteriaDocument, {
    variables: { url: `${baseUrl}${searchUrl}` },
  });

  const search = data?.serpDecodeSearchCriteria;

  if (loading || error || !search) {
    return <CustomLoader />;
  }

  return (
    <Stack space="small">
      {search.keywords !== null && <Keywords keywords={search.keywords} />}
      <OpportunityType opportunityTypes={search.opportunityTypes} />
      {search.investmentLevel !== null &&
        search.investmentLevel.max !== null &&
        search.investmentLevel.min !== null && (
          <Value
            max={search.investmentLevel.max}
            min={search.investmentLevel.min}
          />
        )}
      {search.industries !== null && (
        <Industry industries={search.industries} />
      )}
      {search.districts !== null && <District districts={search.districts} />}
    </Stack>
  );
};

export default SavedSearches;
